import React, { useEffect, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';
import { formDataValidate } from '../../../../../utils/formUtils';
import {
  createTelephonyExtension,
  updateTelephonyExtension,
} from '../../../../../utils/ServicesAPIUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import { fetchAllTelephonyDetails } from '../../../servicesSlice';
import { Button, IconButton } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CustomTextField from '../../../../../shared/form-controls/custom-text-field/Custom-Text-Field';
import LinearProgressBar from '../../../../../shared/progress-bar/Progress';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../../utils/loaderSlice';

const ExtensionsCreate = (props) => {
  const { toggleDrawer, hotelId, extensionObj, showLoader = () => {} } = props;
  const [loading, setLoading] = useState(false);
  const [isTabInValid, setIsTabInValid] = useState(true);
  const dispatch = useDispatch();
  const [formData, setFormData] = useReducer((state, newState) => ({ ...state, ...newState }), {
    roomNumber: {
      value: '',
      fieldName: 'Room Number',
      errors: {},
      validations: ['required', 'validAlphaNumeric'],
      tab: 0,
    },
    extensionNumber: {
      value: '',
      fieldName: 'Extension Number',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    password: {
      value: '',
      fieldName: 'Password',
      errors: {},
      validations: ['required', 'validPureAlphaNumeric'],
      tab: 0,
    },
  });

  useEffect(() => {
    if (extensionObj) {
      const currFields = { ...formData };
      currFields.roomNumber.value = extensionObj.roomNumber;
      currFields.extensionNumber.value = extensionObj.extensionNumber;
      currFields.password.value = extensionObj.password;
      setFormData(currFields);
    }
  }, [extensionObj]);

  const validate = () => {
    const updatedFields = {};
    const isValid = formDataValidate(formData, updatedFields);
    setFormData(updatedFields);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (hotelId) {
      if (validate()) {
        try {
          dispatch(stepUpLoaderCount());
          toggleDrawer('', false, '');
          const selectedLanguage = decryptLocalStorage('lang');
          const language = selectedLanguage !== 'en' ? selectedLanguage : '';
          setLoading(true);
          const payload = {
            roomNumber: formData.roomNumber.value,
            extensionNumber: formData.extensionNumber.value,
            password: formData.password.value,
            hotelId: hotelId,
          };
          if (extensionObj) {
            payload.id = extensionObj.id;
            await updateTelephonyExtension(payload, language);
            notificationManager('success', 'Extension updated successfully');
          } else {
            await createTelephonyExtension(payload, language);
            notificationManager('success', 'Extension created successfully');
          }
          dispatch(fetchAllTelephonyDetails(hotelId));
          onCancel(e);
        } catch (error) {
          if (error?.graphQLErrors) {
            notificationManager('danger', error?.graphQLErrors?.[0]?.message);
          } else {
            extensionObj
              ? notificationManager('danger', 'Failed to update Extension')
              : notificationManager('danger', 'Failed to create Extension');
          }
        } finally {
          setLoading(false);
          dispatch(stepDownLoaderCount());
        }
        setLoading(false);
      }
    }
  };

  const onCancel = (e) => {
    toggleDrawer('', false, '');
  };

  useEffect(() => {
    const isValid = formDataValidate(formData, false);
    setIsTabInValid(!isValid);
  }, [formData]);

  return (
    <form onSubmit={handleSubmit} className='category-create' noValidate>
      <div className='container pt-4 d-flex align-items-end flex-column '>
        <div className='row px-3 w-100 mt-4'>
          <div className='col-md-6'>
            <span className='page-title'>{extensionObj ? 'Edit Extension' : 'Add Extension'}</span>
          </div>
          <div className='col-md-6 d-flex justify-content-end'>
            <IconButton aria-label='delete' onClick={(e) => onCancel(e)}>
              <CancelIcon />
            </IconButton>
          </div>
        </div>
        <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
          <div className='row px-3 w-100 mt-4'>
            <div className='col-md-6'>
              <span className='page-title-secondary'>Room & Extension</span>
            </div>
          </div>
          <div className='row px-3 w-100 d-flex mt-3'>
            <div className='row'>
              <div className='col-md-6 mb-3'>
                <CustomTextField
                  item={formData.roomNumber}
                  handleInputChange={(e) => {
                    setFormData({ roomNumber: e });
                  }}
                  maxLength={6}
                  endAdornment={true}
                  characterCount={true}
                />
              </div>
              <div className='col-md-6 mb-3'>
                <CustomTextField
                  item={formData.extensionNumber}
                  handleInputChange={(raw) => {
                    const str = (raw?.value ?? '')?.toString();
                    const digits = str?.replace(/\D/g, '')?.slice(0, 6);
                    setFormData({
                      extensionNumber: { ...raw, value: digits },
                    });
                  }}
                  type='number'
                  maxLength={6}
                  inputProps={{
                    min: 0,
                    max: 999999,
                    step: 1,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    onKeyDown: (e) => {
                      if (
                        ['.', 'Decimal', 'e', 'E', '+', '-']?.includes(e?.key) ||
                        e?.code === 'NumpadDecimal'
                      ) {
                        e?.preventDefault();
                      }
                    },
                    onBeforeInput: (e) => {
                      if (e?.data === '.') {
                        e?.preventDefault();
                      }
                    },
                    onPaste: (e) => {
                      const pasted = e?.clipboardData?.getData('text');
                      if (/\D/?.test(pasted)) {
                        e?.preventDefault();
                      }
                    },
                  }}
                  endAdornment={true}
                  characterCount={true}
                />
              </div>
            </div>
            <div className='row'>
              <div className='col-md-6 mb-3'>
                <CustomTextField
                  item={formData.password}
                  handleInputChange={(e) => {
                    setFormData({ password: e });
                  }}
                  maxLength={8}
                  endAdornment={true}
                  characterCount={true}
                />
              </div>
            </div>
          </div>
        </div>
        <div className='row mx-2 mt-auto px-4 w-100 pb-5'>
          <div className='col-md-12 d-flex justify-content-start'>
            <Button
              variant='contained'
              type='submit'
              color='primary'
              className='me-3'
              size='large'
              disabled={isTabInValid || loading}
            >
              {extensionObj ? 'Update' : 'Add'}
            </Button>
            <Button
              disabled={loading}
              variant='outlined'
              type='button'
              color='secondary'
              size='large'
              onClick={(e) => onCancel(e)}
            >
              Cancel
            </Button>
          </div>
          {loading && <div className='pt-4 col-md-12'>{<LinearProgressBar />}</div>}
        </div>
      </div>
    </form>
  );
};

export default ExtensionsCreate;
